import { z } from 'zod';

import { instante, listOutput, page } from './common';
import { defineEndpoint } from './endpoint';
import { EscolhaDeTitular, TitularDaVenda } from './vendas';

// Doc 03 §9. Saídas de leitura preservam as chaves snake_case das RPCs (telas grandes as consomem, §11);
// entradas novas em camelCase.

const texto = z.string().nullable();
const IdContrato = z.object({ id: z.string().min(1).max(64) });

export const StatusContrato = z.enum(['rascunho', 'pendente', 'ativo', 'cancelado', 'aguardando_renovacao', 'cancelamento_em_analise']);
export const ServiceType = z.enum(['solar', 'eletroposto']);
export type ServiceType = z.infer<typeof ServiceType>;

// ── Contratos: leitura ─────────────────────────────────────────────────────────────────────────────

/** Linha de `list_contracts` (10 args, baseline.sql:8261-8306; inclusive o typo `link_primera_cobranca`). */
export const ContratoLinha = z.object({
  id: z.string(),
  nome: texto,
  documento: texto,
  email: texto,
  created_at: instante.nullable(),
  status: texto,
  assinatura_status: texto,
  boleto_url: texto,
  assinatura_url: texto,
  documento_url: texto,
  documento_assinado_url: texto,
  valor_mensal: z.number().nullable(),
  valor_anual: z.number().nullable(),
  grupo: texto,
  created_by: z.uuid().nullable(),
  created_by_nome: texto,
  gateway_pagamento: texto,
  titular: TitularDaVenda,
  partner_id: z.uuid().nullable(),
  parceiro_nome: texto,
  pago: z.boolean().nullable(),
  service_type: z.string(),
  payment_version: texto,
  status_financeiro: z.enum(['inadimplente', 'em_dia']),
  primeira_cobranca_gerada: z.boolean(),
  link_primera_cobranca: texto,
  /** Adição deliberada (decisão de main): o filtro "bonificados" da lista lia este campo e o RPC não o trazia. */
  is_bonificado: z.boolean(),
  /** Adição deliberada (coluna Sistema da lista): potência como digitada (kWp da planta solar; kW do equipamento do eletroposto). */
  potencia: texto,
  /** Adições deliberadas (lista de contratos 2026-10-28): onde está o sistema, até quando vale, quanto está vencido. */
  cidade: texto,
  uf: texto,
  vigencia_fim: z.iso.date().nullable(),
  debito_vencido: z.number(),
  cancelado_em: instante.nullable(),
});
export type ContratoLinha = z.infer<typeof ContratoLinha>;

/** Janelas dos atalhos da lista: contrato que termina o ciclo nestes dias e rascunho sem movimento há tantos dias. */
export const RENOVACAO_PROXIMA_DIAS = 60;
export const RASCUNHO_PARADO_DIAS = 7;

export const PendenciaContrato = z.enum(['pagos_aguardando_assinatura', 'parados_sem_pagamento', 'renovacao_proxima', 'rascunho_parado']);
export type PendenciaContrato = z.infer<typeof PendenciaContrato>;

export const OrdemContratos = z.enum(['recentes', 'vigencia', 'valor', 'nome']);
export type OrdemContratos = z.infer<typeof OrdemContratos>;

export const FiltrosListaContratos = z.object({
  ...page,
  search: z.string().trim().min(1).optional(),
  userId: z.uuid().optional(),
  /** Um parceiro ou a venda da Aliança. */
  parceiroId: EscolhaDeTitular.optional(),
  clienteDocumento: z.string().min(1).optional(),
  status: z.string().min(1).optional(),
  gateway: z.string().min(1).optional(),
  statusFinanceiro: z.enum(['inadimplente', 'em_dia']).optional(),
  serviceType: ServiceType.optional(),
  /** Critério em SQL (`comPendencia`), o mesmo na contagem dos atalhos e na lista. */
  pendencia: PendenciaContrato.optional(),
  /** Dias de emissão em São Paulo, inclusivos. */
  emitidoDe: z.iso.date().optional(),
  emitidoAte: z.iso.date().optional(),
  bonificacao: z.enum(['bonificados', 'normais']).optional(),
  ordem: OrdemContratos.optional(),
});
export type FiltrosListaContratos = z.infer<typeof FiltrosListaContratos>;

export const listarContratos = defineEndpoint({
  method: 'GET',
  path: '/api/contratos',
  query: FiltrosListaContratos,
  output: listOutput(ContratoLinha),
});

/** Contagens dos atalhos "Precisa de você" da lista, no escopo do ator. */
export const AtalhosContratos = z.object({
  pagosAguardandoAssinatura: z.number().int(),
  parcelaVencida: z.object({ total: z.number().int(), valor: z.number() }),
  renovacaoProxima: z.object({ total: z.number().int(), proximoFim: z.iso.date().nullable() }),
  rascunhoParado: z.object({ total: z.number().int(), maisAntigoEm: instante.nullable() }),
});
export type AtalhosContratos = z.infer<typeof AtalhosContratos>;

export const atalhosContratos = defineEndpoint({
  method: 'GET',
  path: '/api/contratos/atalhos',
  query: z.object({ serviceType: ServiceType.optional() }),
  output: AtalhosContratos,
});

const Opcao = z.object({ id: z.uuid(), nome: z.string().nullable() });

export const filtrosContratos = defineEndpoint({
  method: 'GET',
  path: '/api/contratos/filtros',
  query: z.object({ parceiroId: EscolhaDeTitular.optional(), serviceType: ServiceType.optional() }),
  output: z.object({ parceiros: z.array(Opcao), users: z.array(Opcao) }),
});

/** `data` de `get_contract_details` (SQL-ATOR, baseline.sql:6230-6316), chaves e ordem preservadas. */
export const ContratoDetalhe = z.object({
  id: z.string(),
  status: texto,
  grupo: texto,
  indice: z.number().nullable(),
  valor_mensal: z.number().nullable(),
  valor_anual: z.number().nullable(),
  valor_sistema: z.number().nullable(),
  created_at: instante.nullable(),
  pago: z.boolean().nullable(),
  gateway_pagamento: texto,
  payment_version: texto,
  boleto_url: texto,
  assinatura_url: texto,
  assinatura_status: texto,
  assinatura_id: z.uuid().nullable(),
  documento_id: z.uuid().nullable(),
  documento_url: texto,
  documento_assinado_url: texto,
  id_pagamento: texto,
  uuid_pagamento: z.uuid().nullable(),
  desconto_avista_aplicado: z.boolean().nullable(),
  assinatura_manual: z.boolean().nullable(),
  assinatura_manual_motivo: texto,
  assinatura_manual_data: instante.nullable(),
  assinatura_manual_por_nome: texto,
  comissao_percentual: z.number().nullable(),
  comissao_valor_estimado: z.number().nullable(),
  parceiro_comissao_habilitada: z.boolean().nullable(),
  cancelado_motivo: texto,
  cancelado_em: instante.nullable(),
  cancelado_por_nome: texto,
  cliente_id: z.uuid().nullable(),
  cliente_nome: texto,
  cliente_documento: texto,
  cliente_email: texto,
  cliente_telefone: texto,
  cliente_concessionaria: texto,
  cliente_cep: texto,
  cliente_logradouro: texto,
  cliente_numero: texto,
  cliente_complemento: texto,
  cliente_bairro: texto,
  cliente_cidade: texto,
  cliente_uf: texto,
  planta_id: z.uuid().nullable(),
  planta_potencia: texto,
  planta_placas: texto,
  planta_modelo_inversor: texto,
  planta_valor_equipamento: texto,
  planta_nota_fiscal: texto,
  planta_data_vistoria: texto,
  criador_id: z.uuid().nullable(),
  criador_nome: texto,
  criador_email: texto,
  titular: TitularDaVenda,
  parceiro_id: z.uuid().nullable(),
  parceiro_nome: texto,
  /** Contrato da API: revendedor informado pelo parceiro (só informativo). */
  revendedor: z.object({ nome: z.string(), documento: z.string().optional() }).nullable(),
  /** A renovação é conduzida pelo parceiro via API (painel só acompanha). Regra: domain/renovacoes/gestao.ts. */
  renovacao_pelo_parceiro: z.boolean(),
});
export type ContratoDetalhe = z.infer<typeof ContratoDetalhe>;

export const lerContrato = defineEndpoint({
  method: 'GET',
  path: '/api/contratos/:id',
  params: IdContrato,
  output: ContratoDetalhe,
});

/** Shape de `get_contract_for_resume` sem o `success` (baseline.sql:6480-6519). */
export const Retomada = z.object({
  contract: z.object({
    id: z.string(),
    status: texto,
    grupo: texto,
    indice: z.number().nullable(),
    valor_mensal: z.number().nullable(),
    valor_anual: z.number().nullable(),
    valor_sistema: z.number().nullable(),
    documento_url: texto,
    partner_id: z.uuid().nullable(),
    comissao_percentual: z.number().nullable(),
  }),
  cliente: z.object({
    nome: texto, documento: texto, email: texto, telefone: texto, concessionaria: texto, cep: texto,
    logradouro: texto, numero: texto, complemento: texto, bairro: texto, cidade: texto, uf: texto,
  }),
  planta: z.object({
    potencia: texto, placas: texto, modelo_inversor: texto, valor_equipamento: texto, nota_fiscal: texto, data_vistoria: texto,
  }),
});
export type Retomada = z.infer<typeof Retomada>;

export const lerRetomada = defineEndpoint({
  method: 'GET',
  path: '/api/contratos/:id/retomada',
  params: IdContrato,
  output: Retomada,
});

// ── Contratos: criação ─────────────────────────────────────────────────────────────────────────────

const opcional = z.string().trim().nullable().optional();

/** Cliente segurado (compartilhado com EV, doc 04 §7). `documento` vai ao banco só com dígitos. */
export const ClienteInput = z.strictObject({
  nome: z.string().trim().min(1),
  documento: z.string().refine((v) => /\d/.test(v), { message: 'Documento inválido' }),
  email: opcional,
  telefone: opcional,
  concessionaria: opcional,
  cep: opcional,
  logradouro: opcional,
  numero: opcional,
  complemento: opcional,
  bairro: opcional,
  cidade: opcional,
  uf: opcional,
});
export type ClienteInput = z.infer<typeof ClienteInput>;

/** Planta solar; `valorEquipamento`/`dataVistoria` são texto no banco (paridade, doc 03 §11). */
export const PlantaInput = z.strictObject({
  potencia: opcional,
  placas: opcional,
  modeloInversor: opcional,
  valorEquipamento: opcional,
  notaFiscal: opcional,
  dataVistoria: opcional,
});
export type PlantaInput = z.infer<typeof PlantaInput>;

const valor = z.number().finite().nonnegative();

export const CriarContratoBody = z.strictObject({
  cliente: ClienteInput,
  planta: PlantaInput,
  cotacao: z.strictObject({ grupo: z.string().min(1), indice: valor, valorMensal: valor, valorAnual: valor, valorSistema: valor }),
  comissaoPercentual: z.number().min(0).lt(1000).nullable().optional(),
  status: z.enum(['rascunho', 'pendente']).default('pendente'),
  propostaId: z.uuid().optional(),
  /** Em nome de quem, para quem escolhe; vindo de proposta, o titular é o dela (spec VENDA-PELA-ALIANCA). */
  titular: EscolhaDeTitular.optional(),
});
export type CriarContratoBody = z.infer<typeof CriarContratoBody>;

export const ContratoCriado = z.object({
  id: z.string(),
  clienteId: z.uuid(),
  plantaId: z.uuid().nullable(),
  partnerId: z.uuid().nullable(),
  comissaoPercentual: z.number().nullable(),
  status: z.enum(['rascunho', 'pendente']),
});
export type ContratoCriado = z.infer<typeof ContratoCriado>;

export const criarContrato = defineEndpoint({
  method: 'POST',
  path: '/api/contratos',
  body: CriarContratoBody,
  output: ContratoCriado,
});

// ── Contratos: comandos com efeito externo (cancelamento, assinatura, emissão) ──────────────────────

/** Corpo atual de `POST /api/contracts/[id]/cancel` (route.ts:13-43), path e envelope preservados (L1). */
export const CancelarContratoBody = z
  .object({
    action: z.enum(['direct_cancel', 'approve_cancel']),
    motivo: z.string().refine((v) => v.trim().length >= 10, { message: 'motivo é obrigatório e deve ter pelo menos 10 caracteres' }),
    password: z.string().min(1),
    /** Aceitos e ignorados (L1): só iam para o n8n `CANCELAMENTO`, que saiu (I33). */
    assinatura_url: z.string().nullable().optional(),
    boleto_url: z.string().nullable().optional(),
    solicitacao_id: z.uuid().optional(),
  })
  .refine((b) => b.action !== 'approve_cancel' || b.solicitacao_id, { message: 'solicitacao_id é obrigatório para approve_cancel' });
export type CancelarContratoBody = z.infer<typeof CancelarContratoBody>;

export const cancelarContrato = defineEndpoint({
  method: 'POST',
  path: '/api/contracts/:id/cancel',
  params: IdContrato,
  body: CancelarContratoBody,
  output: z.object({ success: z.literal(true), warnings: z.record(z.string(), z.unknown()).optional() }),
});

/**
 * `POST /api/contratos/emitir-documento` (path e envelope `{success, data:{documentoUrl}}` preservados). O
 * `payload` antigo ainda é aceito e IGNORADO: o PDF é montado do banco (C22).
 */
export const emitirDocumento = defineEndpoint({
  method: 'POST',
  path: '/api/contratos/emitir-documento',
  body: z.object({ contractId: z.string().min(1).max(64), serviceType: ServiceType.optional(), payload: z.unknown().optional() }),
  output: z.object({ documentoUrl: z.string() }),
});

/**
 * `POST /api/contracts/:id/signature` (develop 0.0.60, path e envelope preservados, L1): envio à Clicksign pelo
 * servidor (antes o navegador chamava o n8n, C12) e, com `resend: true`, o reenvio (R7). `n8nPayload` do front
 * antigo é aceito e ignorado. Documento pendente já criado ⇒ devolve o mesmo envelope (C22).
 */
export const enviarAssinatura = defineEndpoint({
  method: 'POST',
  path: '/api/contracts/:id/signature',
  params: IdContrato,
  body: z.object({ resend: z.boolean().optional(), n8nPayload: z.unknown().optional() }),
  output: z.object({ assinatura: z.string(), id_assinatura: z.string().nullable(), id_documento: z.string().nullable() }),
});

/** `POST /api/manual-signature` (multipart `{file, contractId, motivo}`, route.ts:35-50). */
export const assinarManualmente = defineEndpoint({
  method: 'POST',
  path: '/api/manual-signature',
  body: z.object({ contractId: z.string().min(1).max(64), motivo: z.string().refine((v) => v.trim() !== '', { message: 'Motivo é obrigatório' }) }),
  output: z.object({ success: z.literal(true), message: z.string(), documentUrl: z.string() }),
});

// ── Anotações ──────────────────────────────────────────────────────────────────────────────────────

/** Linha de `listar_anotacoes_contrato` (B:8636-8650). `anexos` volta como gravado (às vezes string JSON). */
export const Anotacao = z.object({
  id: z.uuid(),
  usuario_id: z.uuid().nullable(),
  usuario_nome: z.string(),
  conteudo: z.string(),
  anexos: z.unknown(),
  created_at: instante,
});
export type Anotacao = z.infer<typeof Anotacao>;

export const listarAnotacoes = defineEndpoint({
  method: 'GET',
  path: '/api/contratos/:id/anotacoes',
  params: IdContrato,
  query: z.object({ ...page }),
  output: listOutput(Anotacao),
});

/** Multipart `{conteudo, files[]}`: upload + insert numa chamada (substitui upload-anotacao + criar_anotacao_contrato). */
export const criarAnotacao = defineEndpoint({
  method: 'POST',
  path: '/api/contratos/:id/anotacoes',
  params: IdContrato,
  body: z.object({ conteudo: z.string().refine((v) => v.trim() !== '', { message: 'Conteúdo é obrigatório.' }) }),
  output: z.object({ id: z.uuid() }),
});

/** Rota antiga (`src/app/api/contrato/upload-anotacao/route.ts`), só upload; sai quando o front migrar. */
export const enviarAnexosAnotacao = defineEndpoint({
  method: 'POST',
  path: '/api/contrato/upload-anotacao',
  body: z.object({ contratoId: z.string().min(1).max(64) }),
  output: z.object({
    success: z.literal(true),
    files: z.array(z.object({ url: z.string(), nome: z.string(), tipo: z.string(), tamanho: z.number().int() })),
  }),
});

// ── Clientes e pré-cliente ─────────────────────────────────────────────────────────────────────────

/** JSON de `search_client_by_document` (B:12263-12393), chaves preservadas. */
export const BuscaCliente = z.object({
  found: z.boolean(),
  source: z.enum(['solarz', 'alianca']).nullable(),
  data: z.record(z.string(), z.string().nullable()).nullable(),
  sistema: z.record(z.string(), z.string().nullable()).nullable(),
  document: z.string().optional(),
});
export type BuscaCliente = z.infer<typeof BuscaCliente>;

export const buscarCliente = defineEndpoint({
  method: 'GET',
  path: '/api/clientes/busca',
  query: z.object({ documento: z.string().trim().min(1) }),
  output: BuscaCliente,
});

/** Campos de `upsert_pre_cliente` (o front envia `src/lib/clients/upsert-pre-cliente.ts:23-42`). */
export const PreClienteInput = z.strictObject({
  cpf: z.string().refine((v) => /\d/.test(v), { message: 'CPF/CNPJ é obrigatório' }),
  nome: opcional, email: opcional, telefone: opcional, concessionaria: opcional, cep: opcional,
  logradouro: opcional, numero: opcional, complemento: opcional, bairro: opcional, cidade: opcional, uf: opcional,
  potencia: opcional, placas: opcional, modeloInversor: opcional, valorEquipamento: opcional, notaFiscal: opcional, dataVistoria: opcional,
});
export type PreClienteInput = z.infer<typeof PreClienteInput>;

export const salvarPreCliente = defineEndpoint({
  method: 'PUT',
  path: '/api/clientes/pre-cliente',
  body: PreClienteInput,
  output: z.object({ cpf: z.string() }),
});

// ── Propostas ──────────────────────────────────────────────────────────────────────────────────────

const IdProposta = z.object({ id: z.uuid() });

/** Linha de `listar_propostas` (B:9440-9530); os `valor_*` saem de `->>` e são texto no legado. */
export const PropostaLinha = z.object({
  id: z.uuid(),
  numero_proposta: texto,
  status: z.string(),
  validade: instante.nullable(),
  service_type: z.string(),
  pdf_url: texto,
  created_at: instante,
  titular: TitularDaVenda,
  parceiro_id: z.uuid().nullable(),
  criado_por: z.uuid().nullable(),
  cliente_nome: texto,
  cliente_documento: texto,
  valor_sistema: texto,
  valor_mensal: texto,
  valor_anual: texto,
  grupo: texto,
  parceiro_nome: texto,
  criado_por_nome: texto,
  /** Adição deliberada (coluna Sistema da lista): `sistema_data.potencia` como digitada. */
  potencia: texto,
  /** Adições deliberadas (lista de propostas 2026-10-28): onde está o cliente e quando virou contrato. */
  cidade: texto,
  uf: texto,
  contrato_id: texto,
  contratada_em: instante.nullable(),
});
export type PropostaLinha = z.infer<typeof PropostaLinha>;

/** Janelas dos atalhos da lista de propostas. */
export const PROPOSTA_EXPIRANDO_DIAS = 7;
export const PROPOSTA_EXPIRADA_RECENTE_DIAS = 30;
export const CONVERSAO_JANELA_DIAS = 90;

export const FilaPropostas = z.enum(['expira_em_breve', 'expirada_recente', 'contratada_no_mes']);
export type FilaPropostas = z.infer<typeof FilaPropostas>;

export const OrdemPropostas = z.enum(['recentes', 'validade', 'valor', 'nome']);
export type OrdemPropostas = z.infer<typeof OrdemPropostas>;

export const FiltrosListaPropostas = z.object({
  ...page,
  status: z.string().min(1).optional(),
  parceiroId: EscolhaDeTitular.optional(),
  usuarioId: z.uuid().optional(),
  search: z.string().trim().min(1).optional(),
  serviceType: ServiceType.optional(),
  /** Dias de criação em São Paulo, inclusivos. */
  criadaDe: z.iso.date().optional(),
  criadaAte: z.iso.date().optional(),
  uf: z.string().length(2).optional(),
  fila: FilaPropostas.optional(),
  ordem: OrdemPropostas.optional(),
});
export type FiltrosListaPropostas = z.infer<typeof FiltrosListaPropostas>;

/** Contagens dos atalhos da lista de propostas, no escopo do ator. */
export const AtalhosPropostas = z.object({
  expiramEmBreve: z.object({ total: z.number().int(), primeiraValidade: instante.nullable() }),
  emNegociacao: z.object({ total: z.number().int(), valorMensal: z.number() }),
  expiradasRecentes: z.object({ total: z.number().int(), valorMensal: z.number() }),
  contratadasNoMes: z.object({ total: z.number().int(), conversaoPercentual: z.number().int().nullable() }),
});

export const atalhosPropostas = defineEndpoint({
  method: 'GET',
  path: '/api/propostas/atalhos',
  query: z.object({ serviceType: ServiceType.optional() }),
  output: AtalhosPropostas,
});

export const listarPropostas = defineEndpoint({
  method: 'GET',
  path: '/api/propostas',
  query: FiltrosListaPropostas,
  output: listOutput(PropostaLinha),
});

export const filtrosPropostas = defineEndpoint({
  method: 'GET',
  path: '/api/propostas/filtros',
  output: z.object({ users: z.array(Opcao), parceiros: z.array(Opcao) }),
});

/** `data` de `get_proposta_detalhes` (B:7316-7340). */
export const PropostaDetalhe = z.object({
  id: z.uuid(),
  numero_proposta: texto,
  service_type: z.string(),
  template_id: z.string(),
  status: z.string(),
  validade: instante.nullable(),
  pdf_url: texto,
  cliente_data: z.unknown(),
  sistema_data: z.unknown(),
  cotacao_data: z.unknown(),
  data: z.unknown(),
  titular: TitularDaVenda,
  parceiro_id: z.uuid().nullable(),
  parceiro_nome: texto,
  criado_por: z.uuid().nullable(),
  criado_por_nome: texto,
  contrato_id: texto,
  created_at: instante,
  updated_at: instante,
});
export type PropostaDetalhe = z.infer<typeof PropostaDetalhe>;

/** Preço anual dividido em base da Aliança e comissão do parceiro (Regras 2.1 + 3.1), calculado no servidor. */
export const PrecoDaProposta = z.object({
  anual: z.number(),
  mensal: z.number(),
  percentual: z.number().nullable(),
  base: z.number(),
  comissao_parceiro: z.number(),
});
export type PrecoDaProposta = z.infer<typeof PrecoDaProposta>;

export const lerProposta = defineEndpoint({
  method: 'GET',
  path: '/api/propostas/:id',
  params: IdProposta,
  output: PropostaDetalhe.extend({ preco: PrecoDaProposta.nullable() }),
});

const campoLivre = z.string().optional().default('');

/**
 * Cliente da proposta (solar e EV): `ClientData` do front (src/lib/types/contract.ts:43). Só a forma; os
 * obrigatórios e as mensagens ficam em `erroClienteProposta` (paridade com validateClientData).
 */
export const ClienteProposta = z.object({
  nome: campoLivre, documento: campoLivre, email: campoLivre, telefone: campoLivre, concessionaria: campoLivre, cep: campoLivre,
  logradouro: campoLivre, numero: campoLivre, complemento: campoLivre, bairro: campoLivre, cidade: campoLivre, uf: campoLivre,
});
export type ClienteProposta = z.infer<typeof ClienteProposta>;

export const SistemaProposta = z.object({
  potencia: campoLivre, placas: campoLivre, modeloInversor: campoLivre, valorEquipamento: campoLivre, notaFiscal: campoLivre, dataVistoria: campoLivre,
});
export type SistemaProposta = z.infer<typeof SistemaProposta>;

export const GerarPropostaBody = z.object({
  cliente: ClienteProposta,
  sistema: SistemaProposta,
  uf: z.string().optional(),
  comissao_percentual: z.number().optional(),
  /** Cotação de onde a proposta saiu (conversão cotação → proposta). */
  cotacao_id: z.uuid().optional(),
  /** Em nome de quem, para quem escolhe (spec VENDA-PELA-ALIANCA). */
  titular: EscolhaDeTitular.optional(),
});
export type GerarPropostaBody = z.infer<typeof GerarPropostaBody>;

/** `POST /api/propostas/gerar-completa` (path e corpo preservados, L1). */
export const gerarPropostaCompleta = defineEndpoint({
  method: 'POST',
  path: '/api/propostas/gerar-completa',
  body: GerarPropostaBody,
  output: z.object({ id: z.uuid(), numero_proposta: z.string(), pdf_url: z.string(), validade: z.string() }),
});

export const cancelarProposta = defineEndpoint({
  method: 'POST',
  path: '/api/propostas/:id/cancelar',
  params: IdProposta,
  output: z.object({}),
});
